import React, { memo, useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { ChevronLeft, ChevronRight, Close, Equalizer } from '@material-ui/icons'
import { Spacings, Text, Devices, Sizes } from '@styles'
import EmptyState from '@base/composite/EmptyState'

// number -> px (new width/minWidth/maxWidth column props); strings pass through unchanged.
// Deliberately different from cellWidth's number-means-percent convention below.
const toCssSize = (value) => (value === undefined || value === null ? undefined : typeof value === 'number' ? `${value}px` : value)

// Shared TH/TD width resolver. An explicit `fixedWidth` (or a `minWidth` floor, since
// table-layout:fixed only consults `width` for initial column allocation, not min-width)
// wins over the legacy `width` (cellWidth) prop so the size is actually enforced.
const resolveWidthCss = ({ width, fixedWidth, minWidth }) => {
	if (fixedWidth !== undefined) return toCssSize(fixedWidth)
	if (minWidth !== undefined) return toCssSize(minWidth)
	return typeof width === 'number' ? `${width}%` : width
}

// ─── Exported styled primitives ──────────────────────────────────────────────

export const TableContainer = styled.div`
	width: 100%;
	max-width: 100%;
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.lg};
	font-family: ${({ theme }) => theme.fontFamily.inter || "'Inter', sans-serif"};
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	background-color: ${({ theme }) => theme.colors.surface.card};
	/* Wide tables (many columns) scroll horizontally inside their own border on small screens. */
	overflow-x: auto;

	/* Scrollable: fixed-height card (explicit tableHeight, or the height left in the parent) so only the rows
	   scroll inside TableBodyWrapper, between the sticky header and the footer. */
	${({ $scrollable, $tableHeight }) =>
		$scrollable &&
		($tableHeight
			? `
		height: ${$tableHeight};
		max-height: ${$tableHeight};
		overflow: hidden;
	`
			: `
		flex: 1 1 0;
		height: 0;
		min-height: 180px;
		overflow: hidden;
	`)}
`

export const BaseTable = styled.table`
	width: 100%;
	min-width: 100%;
	text-align: left;

	${({ $scrollable }) =>
		$scrollable
			? `
		border-collapse: separate;
		border-spacing: 0;
	`
			: `
		overflow-x: auto;
		border-collapse: collapse;
		scrollbar-width: none;
		-ms-overflow-style: none;
		&::-webkit-scrollbar {
			display: none;
		}
	`}

	/* Full page of rows (or the empty state) in a scrollable table: stretch the rows to fill the body
	   (no white space under the last row; "No data found" sits centered in the body). */
	${({ $fillHeight }) => $fillHeight && `height: 100%;`}

	${({ $fixedLayout, $mobileAdaptive }) =>
		$fixedLayout &&
		($mobileAdaptive
			? `
		@media ${Devices.tablet} {
			table-layout: fixed;
		}
	`
			: `
		table-layout: fixed;
	`)}
`

export const ScrollableTableWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	overflow: hidden;
	min-height: 0;
`

export const TableBodyWrapper = styled.div`
	flex: 1;
	min-height: 0;
	overflow: auto;
	scrollbar-width: none;
	-ms-overflow-style: none;

	/* Custom scrollbar styling (vertical only, WebKit/Chromium browsers) */
	&::-webkit-scrollbar {
		width: ${Spacings.SPACING_1B};
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}
	&::-webkit-scrollbar-thumb {
		background: ${({ theme }) => theme.colors.border.default};
		border-radius: ${({ theme }) => theme.radius.xs};

		&:hover {
			background: ${({ theme }) => theme.colors.text.secondary};
		}
	}

	/* Hide only the horizontal scrollbar; horizontal scrolling stays fully functional */
	&::-webkit-scrollbar:horizontal {
		display: none;
		height: 0;
	}
`

export const THead = styled.thead`
	background-color: ${({ theme }) => theme.colors.surface.overlay};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

	/* Firefox spreads a height:100% table's leftover space ($fillHeight) across every row, header included.
	   A 1px row height acts as a minimum, so the header keeps its content height and the body rows take the rest. */
	& > tr {
		height: 1px;
	}
`

export const TH = styled.th`
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
	font-size: ${Text.SMALL};
	font-style: ${Text.REGULAR};
	font-weight: ${Text.BOLD_400};
	color: ${({ theme }) => theme.colors.text.secondary};
	// text-transform: uppercase;
	letter-spacing: 0.04em;
	text-align: ${({ align }) => align || 'left'};
	width: ${resolveWidthCss};
	min-width: ${({ minWidth }) => toCssSize(minWidth)};
	max-width: ${({ maxWidth }) => toCssSize(maxWidth)};
	display: ${({ mobile }) => (mobile === false ? 'none' : 'table-cell')};
	overflow: ${({ $fixedLayout }) => ($fixedLayout ? 'hidden' : 'visible')};

	${({ $mobileAdaptive }) =>
		$mobileAdaptive &&
		`
		width: auto;
		min-width: 0;
		max-width: none;
	`}

	${({ $fitContent }) =>
		$fitContent &&
		`
		width: 1%;
		min-width: 0;
		white-space: nowrap;
	`}

	${({ $stickyHeader, theme }) =>
		$stickyHeader &&
		`
		position: sticky;
		top: 0;
		z-index: 2;
		background-color: ${theme.colors.surface.overlay};
	`}

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
		display: table-cell;

		${(props) =>
			props.$mobileAdaptive &&
			`
			width: ${resolveWidthCss(props)};
			min-width: ${toCssSize(props.minWidth)};
			max-width: ${toCssSize(props.maxWidth)};
		`}
	}
`

export const TD = styled.td`
	padding: ${Spacings.SPACING_3B};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
	vertical-align: middle;
	text-align: ${({ align }) => align || 'left'};
	width: ${resolveWidthCss};
	min-width: ${({ minWidth }) => toCssSize(minWidth)};
	max-width: ${({ maxWidth }) => toCssSize(maxWidth)};
	display: ${({ mobile }) => (mobile === false ? 'none' : 'table-cell')};
	overflow: ${({ overflow, $fixedLayout }) => overflow ?? ($fixedLayout ? 'hidden' : 'initial')};
	text-overflow: ${({ overflow }) => (overflow ? 'ellipsis' : 'initial')};

	${({ $mobileAdaptive }) =>
		$mobileAdaptive &&
		`
		width: auto;
		min-width: 0;
		max-width: none;
	`}

	${({ $fitContent }) =>
		$fitContent &&
		`
		width: 1%;
		min-width: 0;
		white-space: nowrap;
	`}

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
		font-size: ${Text.SMALL};
		display: table-cell;

		${(props) =>
			props.$mobileAdaptive &&
			`
			width: ${resolveWidthCss(props)};
			min-width: ${toCssSize(props.minWidth)};
			max-width: ${toCssSize(props.maxWidth)};
		`}
	}
`

export const TR = styled.tr`
	cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
	opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
	transition: background-color 0.2s ease-in-out;

	&:hover {
		background-color: ${({ theme, clickable }) => (clickable ? theme.colors.brand.subtle : 'transparent')};
	}
`

const THContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: ${({ $align }) => ($align === 'right' ? 'flex-end' : $align === 'center' ? 'center' : 'flex-start')};

	gap: ${Spacings.SPACING_1B};
	cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
	user-select: none;

	&:hover {
		color: ${({ $sortable, theme }) => $sortable && theme.colors.brand.primary};
	}
`

const SortIconWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: ${({ active, theme }) => (active ? theme.colors.brand.primary : theme.colors.text.secondary)};
	opacity: ${({ active }) => (active ? 1 : 0.6)};
	transition: opacity 0.15s ease, color 0.15s ease;

	${THContent}:hover & {
		opacity: 1;
	}
`

export const TableActionsWrapper = styled.div`
	display: flex;
	gap: ${Spacings.SPACING_2B};
	align-items: center;
	// justify-content: flex-end;
`

// Row content for action columns and right/center aligned columns.
const AlignedCellContent = styled.div`
	display: flex;
	flex-direction: column;
	/* In a column flex, the cross axis is horizontal → align-items controls left/right.
	   Right-aligned cells (actions, amounts) must line up with their right-aligned header. */
	align-items: ${({ $align }) => ($align === 'center' ? 'center' : 'flex-end')};
	justify-content: center;
`

export const TableActionButton = styled.button`
	background: none;
	border: none;
	color: ${({ theme }) => theme.colors.text.secondary};
	cursor: pointer;
	padding: ${Spacings.SPACING_1B};
	display: flex;
	align-items: center;
	font-family: inherit;

	&:hover:not(:disabled) {
		color: ${({ color, theme }) => color || theme.colors.brand.primary};
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.4;
	}

	svg {
		font-size: ${Text.X_LARGE};
	}
`

const FooterRow = styled.div`
	background: ${({ theme }) => theme.colors.surface.card};
	border-top: 1px solid ${({ theme }) => theme.colors.border.default};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: inherit;
	position: sticky;
	bottom: 0;
	width: 100%;
	box-sizing: border-box;
	flex-shrink: 0;
	margin-top: auto;
	box-shadow: 0 0 ${Spacings.SPACING_5B} 0.125rem rgb(0 0 0 / 5%);

	@media ${Devices.tablet} {
		justify-content: flex-end;
		box-shadow: none;
	}
`

const ItemsShown = styled.span`
	margin-right: ${Spacings.SPACING_4B};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

const PaginationButtonWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
`

const PaginateButton = styled.button`
	height: ${Spacings.SPACING_8B};
	width: ${Spacings.SPACING_8B};
	display: flex;
	align-items: center;
	justify-content: center;
	border-radius: ${({ theme }) => theme.radius.full};
	border: none;
	background: none;
	cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
	color: ${({ disabled, theme }) => (disabled ? theme.colors.text.disabled : theme.colors.text.primary)};
	transition: all 0.2s ease-in-out;
	font-family: inherit;

	&:hover {
		background-color: ${({ disabled, theme }) => !disabled && theme.colors.surface.overlay};
	}

	svg {
		font-size: ${Text.X_LARGE};
	}
`

const EmptyCell = styled.td`
	text-align: center;
	vertical-align: middle;
	padding: ${Spacings.SPACING_4B};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
	font-weight: ${Text.BOLD_500};
`

// Per-row message (v1 `message` column contract), rendered as a full-width row under its source row.
const MessageCell = styled.td`
	padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_4B};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
	background-color: ${({ theme }) => theme.colors.surface.overlay};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

const MessageContent = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_4B};
`

const MessageText = styled.div`
	flex-grow: 1;
	overflow: auto;
	max-width: 100%;
`

const MessageClose = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	color: ${({ theme }) => theme.colors.text.secondary};
`

export const LoadingSkeleton = styled.div`
	min-height: ${Text.M_LARGE};
	background: linear-gradient(
		to right,
		${({ theme }) => theme.colors.surface.overlay} 20%,
		${({ theme }) => theme.colors.border.default} 50%,
		${({ theme }) => theme.colors.surface.overlay} 80%
	);
	background-size: 500px 100px;
	animation: shimmer-v2 1s linear infinite;
	border-radius: ${({ theme }) => theme.radius.xs};

	@keyframes shimmer-v2 {
		0% {
			background-position: -250px 0;
		}
		100% {
			background-position: 250px 0;
		}
	}
`

// ─── TableV2 Component ────────────────────────────────────────────────────────

const TableV2 = memo(
	({
		headers,
		columns,
		data = [],
		loading,
		rowsToDisplay = 10,
		totalItemCount = 0,
		page = 0,
		onPageChange,
		onRowClick,
		hideFooter,
		hideHeader,
		rowClickable = true,
		// optional (row) => boolean: that row is faded and not clickable
		isRowDisabled,
		emptyState,
		sortBy,
		sortDirection,
		onSort,
		children,
		scrollable = false,
		tableHeight,
		// v1 Table props
		id,
		fullHeight,
		noScroll,
		noShadow,
		noRoundBorder,
		...props
	}) => {
		const [internalSortKey, setInternalSortKey] = useState(null)
		const [internalSortDir, setInternalSortDir] = useState(null)
		const [message, setMessage] = useState({})

		// v1 `fullHeight` (grow + scroll rows internally) maps onto v2's scrollable layout; `noScroll` opts out.
		const isScrollable = (scrollable || fullHeight) && !noScroll

		// ── columns mode (auto rows) ───────────────────────────────────────────
		const activeColumns = (columns || []).filter((col) => col?.exportOnly !== true)
		// A `fitContent` column needs the browser's auto layout to size it to its content, so it opts the table out of fixed layout
		// (the other columns' width/minWidth/maxWidth then act as preferred/minimum sizes).
		const hasFitContentColumn = activeColumns.some((col) => col.fitContent)
		const hasFixedWidthColumn =
			!hasFitContentColumn && activeColumns.some((col) => col.width !== undefined || col.minWidth !== undefined || col.maxWidth !== undefined)
		// v1 mobile rule (only truthy `mobile` columns show below tablet) applies once any column opts in with a truthy `mobile`.
		const v1MobileMode = activeColumns.some((col) => !!col?.mobile)
		const hasMobileHiddenColumn = activeColumns.some((col) => !isMobileVisible(col, v1MobileMode))
		const mobileAdaptive = hasFixedWidthColumn && hasMobileHiddenColumn

		// v1 cellWidth split: only when some column sets cellWidth, recalculated across the tablet breakpoint like v1.
		const hasCellWidthColumn = activeColumns.some((col) => col?.cellWidth !== undefined)
		const [isBelowTablet, setIsBelowTablet] = useState(() => typeof window !== 'undefined' && window.innerWidth < Sizes.tablet)
		useEffect(() => {
			if (!hasCellWidthColumn) return
			const resizeHandler = () => setIsBelowTablet(window.innerWidth < Sizes.tablet)
			resizeHandler()
			window.addEventListener('resize', resizeHandler)
			return () => window.removeEventListener('resize', resizeHandler)
		}, [hasCellWidthColumn])
		const splitWidth = hasCellWidthColumn
			? getSplitCellWidth(isBelowTablet ? activeColumns.filter((col) => isMobileVisible(col, v1MobileMode)) : activeColumns)
			: undefined

		const sortedData = useMemo(() => {
			if (!data) return []
			if (onSort) return data
			if (!internalSortKey || !internalSortDir) return data

			const sorted = [...data]
			const col = activeColumns.find((c) => {
				return getColumnSortKey(c) === internalSortKey
			})

			sorted.sort((a, b) => {
				let valA = resolveCellValue(col, a)
				let valB = resolveCellValue(col, b)

				// Fallback to rendered content if raw value is an object (like when key is (data) => data)
				if (typeof valA === 'object' && valA !== null) {
					valA = resolveCellContent(col, a)
				}
				if (typeof valB === 'object' && valB !== null) {
					valB = resolveCellContent(col, b)
				}

				const strA = getStringValue(valA).trim()
				const strB = getStringValue(valB).trim()

				// Check if both values represent numbers (stripping currency symbols, commas, and percentage signs)
				const numA = Number(strA.replace(/[^\d.-]/g, ''))
				const numB = Number(strB.replace(/[^\d.-]/g, ''))
				const isNumA = !isNaN(numA) && strA !== ''
				const isNumB = !isNaN(numB) && strB !== ''

				if (isNumA && isNumB) {
					return internalSortDir === 'asc' ? numA - numB : numB - numA
				}

				const comparison = strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' })
				return internalSortDir === 'asc' ? comparison : -comparison
			})

			return sorted
		}, [data, internalSortKey, internalSortDir, onSort, activeColumns])

		// ── headers + children mode (manual rows) ──────────────────────────────
		if (!columns) {
			return (
				<TableContainer
					id={id}
					$scrollable={isScrollable}
					$tableHeight={tableHeight}
					$noRoundBorder={noRoundBorder}
					$fullHeight={isScrollable && fullHeight}
					{...props}
				>
					{isScrollable ? (
						<ScrollableTableWrapper $fullHeight={isScrollable && fullHeight} $noRoundBorder={noRoundBorder}>
							<TableBodyWrapper $fullHeight={isScrollable && fullHeight} $noRoundBorder={noRoundBorder}>
								<BaseTable $scrollable={isScrollable}>
									{headers && renderHeadersArray(headers, isScrollable)}
									<tbody>{children}</tbody>
								</BaseTable>
							</TableBodyWrapper>
						</ScrollableTableWrapper>
					) : (
						<BaseTable>
							{headers && renderHeadersArray(headers)}
							<tbody>{children}</tbody>
						</BaseTable>
					)}
				</TableContainer>
			)
		}

		const renderTableHeader = () => (
			<THead>
				<TR>
					{activeColumns.map((col, index) => {
						const isAction = isActionColumn(col)
						const isSortable = col.sortable !== false && !isAction
						const sortKey = getColumnSortKey(col)
						const currentSortKey = onSort ? sortBy : internalSortKey
						const currentSortDir = onSort ? sortDirection : internalSortDir
						const isSorted = currentSortKey === sortKey

						const handleHeaderClick = () => {
							if (!isSortable) return

							let nextDir = 'asc'
							if (isSorted) {
								if (currentSortDir === 'asc') nextDir = 'desc'
								else if (currentSortDir === 'desc') nextDir = null
							}

							if (onSort) {
								onSort(sortKey, nextDir)
							} else {
								setInternalSortKey(nextDir ? sortKey : null)
								setInternalSortDir(nextDir)
							}
						}

						return (
							<TH
								key={`th-${index}`}
								align={isAction ? 'right' : col.align}
								width={col.cellWidth ?? splitWidth}
								fixedWidth={col.width}
								$fitContent={col.fitContent}
								minWidth={col.minWidth}
								maxWidth={col.maxWidth}
								mobile={isMobileVisible(col, v1MobileMode)}
								$mobileAdaptive={mobileAdaptive}
								$fixedLayout={hasFixedWidthColumn}
								$stickyHeader={isScrollable}
							>
								<THContent $sortable={isSortable} $align={isAction ? 'right' : col.align} onClick={handleHeaderClick}>
									{col.label}
									{isSortable && (
										<SortIconWrapper active={isSorted}>
											<svg
												width='12'
												height='12'
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2.5'
												strokeLinecap='round'
												strokeLinejoin='round'
											>
												<path d='M7 10l5-5 5 5' opacity={isSorted && currentSortDir === 'desc' ? 0.2 : 1} />
												<path d='M7 14l5 5 5-5' opacity={isSorted && currentSortDir === 'asc' ? 0.2 : 1} />
											</svg>
										</SortIconWrapper>
									)}
								</THContent>
							</TH>
						)
					})}
				</TR>
			</THead>
		)

		const renderLoadingRows = () =>
			[...Array(rowsToDisplay).keys()].map((_, index) => (
				<TR key={`loading-${index}`}>
					<TD colSpan={Math.max(activeColumns.length, 1)}>
						<LoadingSkeleton />
					</TD>
				</TR>
			))

		const renderEmptyState = () => (
			<TR>
				<EmptyCell colSpan={Math.max(activeColumns.length, 1)}>
					{fullHeight ? (
						<EmptyState
							icon={emptyState?.icon || <Equalizer />}
							buttonHandler={emptyState?.buttonHandler}
							noAction={emptyState?.noAction ?? true}
							buttonText={emptyState?.buttonText}
							text={emptyState?.text || 'No data found'}
						/>
					) : (
						emptyState?.text || 'No data found'
					)}
				</EmptyCell>
			</TR>
		)

		const closeMessageHandler = (e) => {
			e.stopPropagation()
			setMessage({})
		}

		const renderMessageRow = (rowId) => {
			if (!message?.show || message?.id !== rowId) return null
			return (
				<TR>
					<MessageCell colSpan={Math.max(activeColumns.length, 1)}>
						<MessageContent>
							<MessageText>{message?.text}</MessageText>
							<MessageClose>
								<Close fontSize='small' onClick={closeMessageHandler} />
							</MessageClose>
						</MessageContent>
					</MessageCell>
				</TR>
			)
		}

		const renderRows = () =>
			sortedData.map((rowData, rowIndex) => {
				const rowId = `row-r${rowIndex}`
				const setMessageHandler = (e, text) => {
					e.stopPropagation()
					setMessage({ id: rowId, show: true, text })
				}
				const rowDisabled = !!isRowDisabled?.(rowData)
				return (
					<React.Fragment key={`row-${rowIndex}`}>
						<TR
							clickable={rowClickable && !!onRowClick && !rowDisabled}
							$disabled={rowDisabled}
							onClick={() => rowClickable && !rowDisabled && onRowClick && onRowClick(rowData)}
						>
							{activeColumns.map((col, colIndex) => {
								const align = isActionColumn(col) ? 'right' : col.align
								const content = resolveCellContent(col, rowData, setMessageHandler)
								return (
									<TD
										key={`td-${rowIndex}-${colIndex}`}
										align={align}
										width={col.cellWidth ?? splitWidth}
										fixedWidth={col.width}
										$fitContent={col.fitContent}
										minWidth={col.minWidth}
										maxWidth={col.maxWidth}
										mobile={isMobileVisible(col, v1MobileMode)}
										overflow={col.overflow}
										$mobileAdaptive={mobileAdaptive}
										$fixedLayout={hasFixedWidthColumn}
									>
										{align === 'right' || align === 'center' ? (
											<AlignedCellContent $align={align}>{content}</AlignedCellContent>
										) : (
											content
										)}
									</TD>
								)
							})}
						</TR>
						{renderMessageRow(rowId)}
					</React.Fragment>
				)
			})

		const renderTableBody = () => {
			if (loading) return renderLoadingRows()
			if (!data || data.length === 0) return renderEmptyState()
			return renderRows()
		}

		const pageChangeHandler = (flag, disabled) => {
			if (disabled) return
			onPageChange && onPageChange(flag)
			setMessage({})
		}

		const renderPaginationFooter = () => {
			if (hideFooter || totalItemCount === 0) return null
			const start = data && data.length ? page * rowsToDisplay + 1 : 0
			const end = Math.min(page * rowsToDisplay + rowsToDisplay, totalItemCount)
			const isLeftDisabled = page === 0
			const isRightDisabled = (page + 1) * rowsToDisplay >= totalItemCount

			return (
				<FooterRow $fullHeight={isScrollable && fullHeight} $noRoundBorder={noRoundBorder}>
					<ItemsShown>
						{start} - {end} of {totalItemCount}
					</ItemsShown>
					<PaginationButtonWrapper>
						<PaginateButton disabled={isLeftDisabled} onClick={() => pageChangeHandler(false, isLeftDisabled)}>
							<ChevronLeft />
						</PaginateButton>
						<PaginateButton disabled={isRightDisabled} onClick={() => pageChangeHandler(true, isRightDisabled)}>
							<ChevronRight />
						</PaginateButton>
					</PaginationButtonWrapper>
				</FooterRow>
			)
		}

		return (
			<TableContainer
				id={id}
				$scrollable={isScrollable}
				$tableHeight={tableHeight}
				$noRoundBorder={noRoundBorder}
				$fullHeight={isScrollable && fullHeight}
				{...props}
			>
				{isScrollable ? (
					<ScrollableTableWrapper $fullHeight={isScrollable && fullHeight} $noRoundBorder={noRoundBorder}>
						<TableBodyWrapper $fullHeight={isScrollable && fullHeight} $noRoundBorder={noRoundBorder}>
							<BaseTable
								$fixedLayout={hasFixedWidthColumn}
								$mobileAdaptive={mobileAdaptive}
								$scrollable={isScrollable}
								$fillHeight={!loading && (!data?.length || data.length >= rowsToDisplay)}
							>
								{!hideHeader && renderTableHeader()}
								<tbody>{renderTableBody()}</tbody>
							</BaseTable>
						</TableBodyWrapper>
						{renderPaginationFooter()}
					</ScrollableTableWrapper>
				) : (
					<>
						<BaseTable $fixedLayout={hasFixedWidthColumn} $mobileAdaptive={mobileAdaptive}>
							{!hideHeader && renderTableHeader()}
							<tbody>{renderTableBody()}</tbody>
						</BaseTable>
						{renderPaginationFooter()}
					</>
				)}
			</TableContainer>
		)
	}
)

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Below tablet: v1 mode shows only truthy `mobile` columns; otherwise only `mobile: false` hides.
const isMobileVisible = (col, v1MobileMode) => (v1MobileMode ? !!col?.mobile : col?.mobile !== false)

// v1 calculateWidth: width left after cellWidth columns, split equally among the rest (as %).
// Non-numeric cellWidths make v1's math NaN (ignored by the browser), so they yield no split here.
const getSplitCellWidth = (columns) => {
	const meta = columns.reduce(
		(acc, item) => {
			if (item?.cellWidth) {
				return { widthLeft: acc.widthLeft - item?.cellWidth, count: acc.count - 1 }
			}
			return acc
		},
		{ widthLeft: 100, count: columns.length }
	)
	const width = meta.widthLeft / meta.count
	return Number.isFinite(width) && width > 0 ? width : undefined
}

// Action columns (by type or by label) are right aligned in both header and rows.
const isActionColumn = (col) => {
	if (!col) return false
	if (col.type === 'action') return true
	return col.label === 'Actions' || col.label === 'Action'
}

// `setMessageHandler` is only used by v1-style `message` columns: renderer(value, setMessage) instead of renderer(value, rowData).
const resolveCellContent = (col, rowData, setMessageHandler) => {
	if (!col || !rowData) return ''
	let cellContent

	if (col.key) {
		if (typeof col.key === 'function') {
			cellContent = col.key(rowData)
		} else {
			cellContent = rowData && typeof rowData.get === 'function' ? rowData.get(col.key) : rowData?.[col.key]
		}
	}

	if (col.renderer && typeof col.renderer === 'function') {
		cellContent = col.message && setMessageHandler ? col.renderer(cellContent, setMessageHandler) : col.renderer(cellContent, rowData)
	}

	if (col.default && !cellContent) {
		cellContent = col.default
	}

	return typeof cellContent === 'boolean' ? '' + cellContent : cellContent
}

const resolveCellValue = (col, rowData) => {
	if (!col || !rowData) return ''
	const key = col.key
	if (!key) {
		return resolveCellContent(col, rowData)
	}
	if (typeof key === 'function') {
		return key(rowData)
	}
	return rowData && typeof rowData.get === 'function' ? rowData.get(col.key) : rowData?.[col.key]
}

const getStringValue = (val) => {
	if (val === null || val === undefined) return ''
	if (typeof val === 'string') return val
	if (typeof val === 'number' || typeof val === 'boolean') return String(val)

	if (typeof val === 'object') {
		if (val.props) {
			if (val.props.children) {
				if (Array.isArray(val.props.children)) {
					return val.props.children.map(getStringValue).join(' ')
				}
				return getStringValue(val.props.children)
			}
			if (val.props.label) return getStringValue(val.props.label)
			if (val.props.value) return getStringValue(val.props.value)
		}
		if (Array.isArray(val)) {
			return val.map(getStringValue).join(' ')
		}
		return ''
	}
	return String(val)
}

const getColumnSortKey = (col) => {
	if (!col) return ''
	if (col.key && typeof col.key === 'string') {
		return col.key
	}
	if (col.label && typeof col.label === 'string') {
		return col.label
	}
	return getStringValue(col.label) || String(col.key)
}

const renderHeadersArray = (headers, sticky) => (
	<THead>
		<TR>
			{headers.map((header, index) => {
				const isObject = typeof header === 'object' && header !== null
				return (
					<TH key={index} style={isObject ? header.style : {}} $stickyHeader={sticky}>
						{isObject ? header.label : header}
					</TH>
				)
			})}
		</TR>
	</THead>
)

export default TableV2

// Table utilities: same named exports as the v1 base Table (Columns, auctionColumns, exportColumns, useTableReducer)
export * as Columns from './columns'
export * as auctionColumns from './auctionColumns'
export * as exportColumns from './exportColumns'
export { default as useTableReducer } from './useTableReducer'
